import { useState } from 'react';
import { visibleOptionLabel } from '@/lib/catalog/optionLabel';
import { Link } from 'react-router';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  toast,
} from '@/components/ui';
import type { AdminRole, OrderLineItem } from '@/lib/api-admin/types';
import { friendlyMessage } from '@/lib/errors';
import {
  ITEM_CANCEL_REASONS,
  useItemCancelMutation,
  useItemRefundDoneMutation,
  type ItemCancelReason,
} from '../hooks/useItemCancelMutations';

/** 결제 완료 후 출고 전만(BE 공통 조건). 출고 후에는 반품 클레임. */
const ITEM_CANCEL_ORDER_STATUSES = new Set(['PAYMENT_CONFIRMED', 'HOLD_FOR_RELEASE', 'READY_FOR_FULFILLMENT']);
const CANCEL_ROLES: ReadonlyArray<AdminRole> = ['OPS_ADMIN', 'CS_AGENT', 'SUPER_ADMIN'];
const REFUND_DONE_ROLES: ReadonlyArray<AdminRole> = ['OPS_ADMIN', 'SUPER_ADMIN'];

type Mode = 'cancel' | 'refund-done';

/**
 * QA-520 상품별 부분취소(order #111). 고른 상품·수량만 취소한다 — 주문은 결제완료 그대로, 모든 상품이 취소돼야 취소완료.
 * [선택 상품 취소] = 취소(환불) 클레임 생성 + 바로 승인 → 환불 리스트에서 [환불 처리](PG 부분 환불).
 * [선택 상품 환불완료 처리] = PG 없이 상태만(무통장 계좌 환불을 끝낸 뒤). 결제 기록은 바뀌지 않는다.
 */
export const OrderItemCancelPanel = ({
  orderId,
  orderStatus,
  items,
  roles,
}: {
  orderId: string;
  orderStatus: string;
  items: ReadonlyArray<OrderLineItem>;
  roles: ReadonlyArray<AdminRole>;
}) => {
  const canCancel = CANCEL_ROLES.some((r) => roles.includes(r));
  const canRefundDone = REFUND_DONE_ROLES.some((r) => roles.includes(r));
  const [qty, setQty] = useState<Record<string, number>>({});
  const [mode, setMode] = useState<Mode | null>(null);
  const [doneClaimId, setDoneClaimId] = useState<string | null>(null);

  if (!ITEM_CANCEL_ORDER_STATUSES.has(orderStatus) || items.length < 2 || (!canCancel && !canRefundDone)) return null;

  const selected = items.filter((i) => (qty[i.orderItemId] ?? 0) > 0);
  const selection = selected.map((i) => ({ orderItemId: i.orderItemId, quantity: qty[i.orderItemId] === i.quantity ? null : qty[i.orderItemId]! }));
  const selectedAmount = selected.reduce((s, i) => s + i.unitPrice.amount * (qty[i.orderItemId] ?? 0), 0);

  const toggle = (item: OrderLineItem, on: boolean) => setQty((q) => ({ ...q, [item.orderItemId]: on ? item.quantity : 0 }));

  return (
    <div className="mt-3 rounded-md border p-3" style={{ borderColor: 'var(--divider-soft)' }} data-testid="order-item-cancel-panel">
      <p className="mb-2 text-[13px] font-medium text-[color:var(--content-strong)]">상품별 취소</p>
      <ul className="mb-2 flex flex-col gap-1.5">
        {items.map((i) => {
          const on = (qty[i.orderItemId] ?? 0) > 0;
          return (
            <li key={i.orderItemId} className="flex flex-wrap items-center gap-2 text-[12px]" data-testid="order-item-cancel-row">
              <Checkbox checked={on} onCheckedChange={(v) => toggle(i, v === true)} aria-label={`${i.productName} 선택`} />
              <span className="min-w-0 flex-1 truncate" title={i.productName}>
                {i.productName}
                {visibleOptionLabel(i.variantLabel) ? <span className="text-[color:var(--content-muted)]"> · {visibleOptionLabel(i.variantLabel)}</span> : null}
              </span>
              {on && i.quantity > 1 ? (
                <Input
                  type="number"
                  min={1}
                  max={i.quantity}
                  value={qty[i.orderItemId] ?? i.quantity}
                  onChange={(e) => setQty((q) => ({ ...q, [i.orderItemId]: Math.max(1, Math.min(i.quantity, Number(e.target.value) || 1)) }))}
                  className="w-16"
                  aria-label={`${i.productName} 취소 수량`}
                />
              ) : null}
              <span className="tabular-nums text-[color:var(--content-muted)]">/ {i.quantity}개</span>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        {canCancel ? (
          <Button size="sm" variant="outline" disabled={selected.length === 0} onClick={() => setMode('cancel')} data-testid="order-item-cancel-open">
            선택 상품 취소
          </Button>
        ) : null}
        {canRefundDone ? (
          <Button size="sm" variant="ghost" disabled={selected.length === 0} onClick={() => setMode('refund-done')} data-testid="order-item-refund-done-open">
            선택 상품 환불완료 처리
          </Button>
        ) : null}
        <span className="text-[11px] text-[color:var(--content-muted)]">
          취소한 상품만 환불되고 주문은 결제완료로 남습니다. 모든 상품이 취소되면 주문이 취소완료가 됩니다.
        </span>
      </div>
      {doneClaimId ? (
        <p role="status" className="mt-2 text-[12px]" style={{ color: 'var(--status-success-fg)' }} data-testid="order-item-cancel-done">
          처리했습니다. <Link to={`/claims/${doneClaimId}`} className="underline">클레임 보기</Link> · 환불은{' '}
          <Link to="/payments/refunds" className="underline">환불 리스트</Link>에서 합니다.
        </p>
      ) : null}

      {mode ? (
        <ItemCancelDialog
          mode={mode}
          orderId={orderId}
          selection={selection}
          selectedCount={selected.length}
          selectedAmount={selectedAmount}
          onClose={() => setMode(null)}
          onDone={(claimId) => {
            setDoneClaimId(claimId);
            setQty({});
            setMode(null);
          }}
        />
      ) : null}
    </div>
  );
};

/** 창 — 열 때만 그려서 처리 훅(mutation)도 그때만 쓴다(주문 상세 테스트·초기 렌더에 QueryClient 의존을 늘리지 않음). */
const ItemCancelDialog = ({
  mode,
  orderId,
  selection,
  selectedCount,
  selectedAmount,
  onClose,
  onDone,
}: {
  mode: Mode;
  orderId: string;
  selection: Array<{ orderItemId: string; quantity: number | null }>;
  selectedCount: number;
  selectedAmount: number;
  onClose: () => void;
  onDone: (claimId: string | null) => void;
}) => {
  const [reason, setReason] = useState<ItemCancelReason>('CHANGE_OF_MIND');
  const [note, setNote] = useState('');
  const [approve, setApprove] = useState(true);
  const [refundedAmount, setRefundedAmount] = useState('');
  const cancel = useItemCancelMutation();
  const refundDone = useItemRefundDoneMutation();
  const pending = cancel.isPending || refundDone.isPending;
  const error = mode === 'cancel' ? cancel.error : refundDone.error;
  const noteRequired = mode === 'refund-done' || reason === 'OTHER';
  const submit = () => {
    if (noteRequired && !note.trim()) return;
    const onSuccess = (r: { claimId: string | null }) => {
      toast.success(mode === 'cancel' ? '선택 상품을 취소 접수했습니다.' : '선택 상품을 환불완료로 처리했습니다.');
      onDone(r.claimId);
    };
    if (mode === 'cancel') cancel.mutate({ orderId, selection, reasonCode: reason, note, approve }, { onSuccess });
    else refundDone.mutate({ orderId, selection, reasonCode: reason, note, refundedAmount: refundedAmount === '' ? null : Number(refundedAmount) }, { onSuccess });
  };
  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-md" data-testid="order-item-cancel-dialog">
        <DialogHeader>
          <DialogTitle>{mode === 'cancel' ? '선택 상품 취소' : '선택 상품 환불완료 처리'}</DialogTitle>
          <DialogDescription>
            {mode === 'cancel'
              ? `고른 상품 ${selectedCount}종(상품금액 ${selectedAmount.toLocaleString()}원)으로 취소(환불) 클레임을 만듭니다. 환불은 환불 리스트에서 처리합니다.`
              : 'PG를 부르지 않고 고른 상품을 환불완료로 맞춥니다. 무통장 등 계좌로 환불을 끝낸 뒤에 쓰세요. 결제 기록은 바뀌지 않습니다.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          <label className="grid gap-1 text-xs text-[color:var(--content-muted)]">
            사유
            <Select value={reason} onChange={(e) => setReason(e.target.value as ItemCancelReason)} data-testid="order-item-cancel-reason">
              {ITEM_CANCEL_REASONS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </label>
          <label className="grid gap-1 text-xs text-[color:var(--content-muted)]">
            메모{noteRequired ? ' (필수)' : ''}
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder={reason === 'OTHER' ? '예: 품절' : ''} data-testid="order-item-cancel-note" />
          </label>
          {mode === 'cancel' ? (
            <label className="inline-flex items-center gap-2 text-xs">
              <Checkbox checked={approve} onCheckedChange={(v) => setApprove(v === true)} />
              바로 승인(환불 리스트로 보내기)
            </label>
          ) : (
            <label className="grid gap-1 text-xs text-[color:var(--content-muted)]">
              실제 환불 금액 (원, 비우면 예상 환불액)
              <Input type="number" min={0} value={refundedAmount} onChange={(e) => setRefundedAmount(e.target.value)} data-testid="order-item-refunded-amount" />
            </label>
          )}
          {error ? (
            <p role="alert" style={{ color: 'var(--status-danger-fg)' }}>
              {friendlyMessage(error, '처리하지 못했습니다.')}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={pending}>
            닫기
          </Button>
          <Button variant="cta" size="sm" onClick={submit} disabled={pending || (noteRequired && !note.trim())} data-testid="order-item-cancel-submit">
            {mode === 'cancel' ? '취소 접수' : '환불완료 처리'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
